import { useCallback, useEffect, useState } from "react";

import { fetchDailyGoal, saveDailyGoal } from "./api";
import { subscribeScoreInvalidations } from "./score";
import type {
  DailyGoalKind,
  DailyGoalResponse,
  ExperienceFeedback,
  ExperienceGuide,
  ExperienceProgress,
  ExperienceReward,
} from "./types";

export function DailyGoalCard() {
  const [payload, setPayload] = useState<DailyGoalResponse | null>(null);
  const [kind, setKind] = useState<DailyGoalKind>("cards");
  const [target, setTarget] = useState(10);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(() => {
    void fetchDailyGoal()
      .then((next) => {
        setPayload(next);
        if (next.goal) {
          setKind(next.goal.kind);
          setTarget(next.goal.target);
        }
      })
      .catch((reason: unknown) => {
        setError(reason instanceof Error ? reason.message : "L’objectif quotidien est indisponible.");
      });
  }, []);

  useEffect(() => {
    load();
    return subscribeScoreInvalidations(load);
  }, [load]);

  if (payload && !payload.enabled) return null;
  const option = payload?.options.find((item) => item.kind === kind);
  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!option || saving || target < option.minimum || target > option.maximum) return;
    setSaving(true);
    setError(null);
    try {
      const next = await saveDailyGoal(kind, target);
      setPayload(next);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "L’objectif n’a pas pu être enregistré.");
    } finally {
      setSaving(false);
    }
  };
  const changeKind = (nextKind: DailyGoalKind) => {
    const nextOption = payload?.options.find((item) => item.kind === nextKind);
    setKind(nextKind);
    if (nextOption) setTarget(nextOption.default);
  };

  return (
    <section className="daily-goal-card dashboard-section" aria-labelledby="daily-goal-title">
      <div className="daily-goal-summary">
        <p className="eyebrow">À votre rythme</p>
        <h2 id="daily-goal-title">Mon objectif du jour</h2>
        {payload?.progress ? <>
          <p aria-live="polite">{payload.progress.reached ? "Objectif atteint. Bravo !" : payload.progress.text}</p>
          <div
            className="daily-goal-progress"
            role="progressbar"
            aria-label={`Progression de l’objectif : ${payload.progress.text}`}
            aria-valuemin={0}
            aria-valuemax={payload.progress.target}
            aria-valuenow={Math.min(payload.progress.completed, payload.progress.target)}
            aria-valuetext={payload.progress.text}
          ><span style={{ width: `${payload.progress.percent}%` }} /></div>
          {!payload.progress.reached && <small>Encore {payload.progress.remaining} {payload.goal?.unit} pour aujourd’hui.</small>}
        </> : <p>Chargement de votre progression…</p>}
      </div>
      {payload?.goal && <form className="daily-goal-form" onSubmit={(event) => void save(event)}>
        <label>Type d’objectif<select value={kind} onChange={(event) => changeKind(event.target.value as DailyGoalKind)}>{payload.options.map((item) => <option key={item.kind} value={item.kind}>{item.label}</option>)}</select></label>
        <label>Cible quotidienne<input type="number" required min={option?.minimum} max={option?.maximum} value={target} onChange={(event) => setTarget(Number(event.target.value))} /></label>
        <button className="button button-primary" type="submit" disabled={saving}>{saving ? "Enregistrement…" : "Enregistrer l’objectif"}</button>
        {error && <small className="game-error" role="alert">{error}</small>}
      </form>}
      {!payload && !error && <span className="loader" aria-hidden="true" />}
      {error && !payload && <p className="game-error" role="alert">{error}</p>}
    </section>
  );
}

export const defaultGuide: ExperienceGuide = {
  steps: ["Démarrer", "Lire la consigne", "Répondre", "Voir la correction", "Résultat"],
  target_count: 1,
  estimated_minutes: 1,
};

export function ActivityGuide({ guide, activeStep }: { guide?: ExperienceGuide; activeStep: number }) {
  const selected = guide ?? defaultGuide;
  return (
    <section className="activity-guide" aria-label="Étapes de l’activité">
      <div>
        <strong>Activité courte</strong>
        <span>{selected.target_count} carte{selected.target_count > 1 ? "s" : ""} · environ {selected.estimated_minutes} min</span>
      </div>
      <ol>
        {selected.steps.map((step, index) => (
          <li className={index < activeStep ? "completed" : index === activeStep ? "active" : ""} aria-current={index === activeStep ? "step" : undefined} key={step}>
            <span aria-hidden="true">{index < activeStep ? "✓" : index + 1}</span>{step}
          </li>
        ))}
      </ol>
    </section>
  );
}

export function SessionProgressBar({ progress }: { progress?: ExperienceProgress }) {
  if (!progress) return null;
  return (
    <div className="session-progress" aria-label={`Progression : ${progress.text}`}>
      <div><strong>Progression</strong><span>{progress.text}</span></div>
      <div className="session-progress-track" role="progressbar" aria-valuemin={0} aria-valuemax={progress.target} aria-valuenow={progress.completed} aria-valuetext={progress.text}>
        <span style={{ width: `${progress.percent}%` }} />
      </div>
    </div>
  );
}

export function FeedbackNotice({ feedback }: { feedback?: ExperienceFeedback | null }) {
  if (!feedback) return null;
  return (
    <div className={`experience-feedback ${feedback.tone}`} role="status" aria-live="polite" aria-atomic="true">
      <strong>{feedback.title}</strong>
      <span>{feedback.message}</span>
      {feedback.correction && <span>Réponse attendue : {feedback.correction}</span>}
    </div>
  );
}

export function AbandonActivityButton({
  disabled,
  onConfirm,
}: {
  disabled?: boolean;
  onConfirm: () => Promise<void>;
}) {
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  if (!confirming) {
    return <button className="button button-secondary" type="button" disabled={disabled} onClick={() => setConfirming(true)}>Quitter l’activité</button>;
  }
  return (
    <div className="abandon-confirmation" role="alertdialog" aria-modal="false" aria-labelledby="abandon-title" aria-describedby="abandon-description">
      <strong id="abandon-title">Quitter cette activité ?</strong>
      <p id="abandon-description">Les réponses déjà enregistrées seront conservées, sans ajouter de résultat ni de point.</p>
      <div>
        <button className="button" type="button" disabled={submitting} onClick={() => setConfirming(false)}>Continuer l’activité</button>
        <button className="button button-danger" type="button" disabled={submitting} onClick={() => { setSubmitting(true); void onConfirm().finally(() => setSubmitting(false)); }}>
          {submitting ? "Arrêt…" : "Confirmer l’abandon"}
        </button>
      </div>
    </div>
  );
}

export function AbandonedActivityPanel({ feedback, onReturn }: { feedback: ExperienceFeedback; onReturn: () => void }) {
  return (
    <div className="memo-panel memo-result abandoned">
      <p className="eyebrow">Session abandonnée</p>
      <h1>Activité interrompue</h1>
      <FeedbackNotice feedback={feedback} />
      <button className="button button-primary" type="button" onClick={onReturn}>Retour à la série</button>
    </div>
  );
}

export function RewardSummary({ reward }: { reward?: ExperienceReward | null }) {
  if (!reward) return null;
  return (
    <section className="reward-summary" aria-label="Récompenses de la session">
      <strong>{reward.message}</strong>
      <div>
        <span>Niveau {reward.level}</span>
        <span>{reward.streak_days} jour{reward.streak_days > 1 ? "s" : ""} de suite</span>
      </div>
      {reward.badges.length > 0 && <ul>{reward.badges.map((badge) => <li key={badge}>{badge}</li>)}</ul>}
    </section>
  );
}
